import { FC, useEffect, useState } from 'react';
import { Button, Form, message, Typography } from 'antd';
import { Trans } from 'react-i18next';
import cn from 'classnames';

import { ModalComponent, RangeSlider } from 'componets/common';
import { ConfirmationModal, Popover, RangeSliderInput, Skeleton } from 'componets/ui';
import { CheckBoxField, FormItem, SubmitButton } from 'componets/form';
import { RiskNotice } from '../../../RiskNotice';

import { useAppDispatch, useAppSelector, usePreventScroll, useTranslate } from 'hooks';

import {
	cancelCounteroffer,
	getCounterofferProperties,
	makeCounteroffer,
} from 'features/secondaryMarket/actions';

import { FieldData, ModalProps } from 'models';

import Utils from 'utils/utils';
import { Rules } from 'utils/constants';
import { language } from 'utils';

import { selectIsAdmin } from 'selectors';

import { ReactComponent as CounterofferIcon } from 'assets/icons/counteroffer.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';

import styles from './index.module.scss';

const WORDS = [
	'pleaseNoteText',
	'yourOfferPriceText',
	'sellersPriceText',
	'counterofferModalText',
	'cancelText',
	'submitCounterOfferText',
	'tooltipCounterOfferText',
	'errorLowInvestorBalanceText',
];

export const SecondaryMarketCounteroffer: FC<{
	id: string;
	pendingCounterOffer: string | null;
}> = ({ id, pendingCounterOffer }) => {
	const { tooltipCounterOfferText, cancelCounterofferText } = useTranslate([
		'tooltipCounterOfferText',
		'cancelCounterofferText',
	]);

	const isAdmin = useAppSelector((state) => state.cookies.isAdmin);

	const [isOpen, setIsOpen] = useState(false);

	const Modal = pendingCounterOffer
		? SecondaryMarketCounterofferCancelModal
		: SecondaryMarketCounterofferModal;

	return (
		<div className={styles.root}>
			<Popover
				content={pendingCounterOffer ? cancelCounterofferText : tooltipCounterOfferText}
				trigger="hover"
				zIndex={98}
			>
				<button onClick={() => setIsOpen(true)}>
					<CounterofferIcon
						className={cn(styles.icon, {
							[styles.red]: !!pendingCounterOffer && !isAdmin,
							[styles.grey]: isAdmin,
						})}
					/>
				</button>
			</Popover>

			{isOpen && (
				<Modal
					id={pendingCounterOffer ? pendingCounterOffer : id}
					onClose={() => setIsOpen(false)}
					isOpen={isOpen}
				/>
			)}
		</div>
	);
};

interface Props extends ModalProps {
	id: string;
	onClose: () => void;
	isOpen: boolean;
}

const SecondaryMarketCounterofferModal: FC<Props> = ({ id, onClose, isOpen }) => {
	const [maskClosable, setMaskClosable] = useState(true);
	const t = useTranslate(WORDS);
	const isAdmin = useAppSelector(selectIsAdmin);

	const isLithuanian = language === 'lt';

	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isOpenAgreeRisk, setIsOpenAgreeRisk] = useState(false);

	const amount = Form.useWatch('amount', form);

	const { counterOffer, overview } = useAppSelector((state) => ({
		counterOffer: state.secondaryMarket.counterOffer,
		overview: state.overview.data,
	}));

	const dispatch = useAppDispatch();

	usePreventScroll();

	useEffect(() => {
		setFields([{ name: ['amount'], value: Number(counterOffer.sellerPrice).toFixed(2) }]);
	}, [counterOffer]);
	const onFinish = () => {
		if (+overview.availableMoney === 0) {
			message.error(t.errorLowInvestorBalanceText);
			return;
		}

		setIsOpenAgreeRisk(true);
	};

	useEffect(() => {
		setIsLoading(true);
		dispatch(getCounterofferProperties(id)).finally(() => {
			setIsLoading(false);
		});
	}, [dispatch, id]);

	const onFinishResult = () => {
		dispatch(makeCounteroffer({ id, amount: amount.toString() })).finally(() => {
			setIsSubmitting(false);
			onClose();
			setIsOpenAgreeRisk(false);
		});
	};

	return (
		<ModalComponent
			className={styles.modal}
			isOpen={isOpen}
			onClose={onClose}
			title={t.tooltipCounterOfferText}
			maskClosable={maskClosable}
		>
			<Skeleton loading={isLoading}>
				<Form onFinish={onFinish} onFieldsChange={setFields} fields={fields} form={form}>
					<div className={styles.row}>
						<div className={styles.column}>
							<Typography.Text type="secondary">{t.yourOfferPriceText}</Typography.Text>

							<RangeSliderInput
								maxPrice={+counterOffer.maxPrice}
								minPrice={+counterOffer.minPrice}
								setFields={setFields}
							/>
						</div>

						<div className={styles.column}>
							<Typography.Text type="secondary" className={styles.price}>
								{t.sellersPriceText}
							</Typography.Text>
							<span className={styles.amount}>{Utils.moneyFormat(+counterOffer.sellerPrice)}</span>
						</div>
					</div>

					<div className={styles.range}>
						<RangeSlider
							value={amount}
							onAfterChange={() => {
								setMaskClosable(false);

								setTimeout(() => setMaskClosable(true), 0);
							}}
							min={+counterOffer.minPrice}
							max={+counterOffer.maxPrice}
							onChange={(data: number) => {
								const value = data.toFixed(2);
								setFields([{ name: ['amount'], value }]);
							}}
							defaultValue={+counterOffer.sellerPrice}
						/>
					</div>

					<div className={styles.column}>
						<span className={styles.noteRow}>
							<InfoIcon />
							{t.pleaseNoteText}
						</span>

						<span className={styles.noteText}>{t.counterofferModalText}</span>
					</div>

					<FormItem
						name="agree"
						rules={[Rules.REQUIRED_CHECKBOX]}
						className={`${styles.agree} checkbox`}
					>
						<CheckBoxField
							text={
								<Trans
									i18nKey="confirmAssigmentAgreementText"
									components={{
										agreement_link: (
											<a
												className="modal-agreeLink"
												href={
													isLithuanian
														? 'https://crowdpear.com/wp-content/uploads/2023/05/Reikalavimo_teises_perleidimo_sutarties_bendrosios_salygos.pdf'
														: 'https://crowdpear.com/wp-content/uploads/2023/05/General_term_and_coditions_of_the_Assignment_agreement.pdf'
												}
												type="button"
												target="_blank"
												rel="noreferrer"
											/>
										),
									}}
								/>
							}
						/>
					</FormItem>

					<div className="modal-footer">
						<SubmitButton
							text={t.submitCounterOfferText}
							classNameWrapper={styles.submitWrapper}
							className={cn(styles.submit, { 'ant-btn-grey': isAdmin })}
							loading={isSubmitting}
						/>
						<Button onClick={onClose} className={styles.cancel}>
							{t.cancelText}
						</Button>
					</div>
				</Form>
			</Skeleton>

			<RiskNotice
				onFinish={onFinishResult}
				loading={isSubmitting}
				isOpen={isOpenAgreeRisk}
				onClose={() => setIsOpenAgreeRisk(false)}
			/>
		</ModalComponent>
	);
};

const SecondaryMarketCounterofferCancelModal: FC<Props> = ({ isOpen, onClose, id }) => {
	const t = useTranslate([
		'yesText',
		'noText',
		'cancelCounterofferText',
		'cancelCounterofferModalText',
	]);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const dispatch = useAppDispatch();

	const onCancel = () => {
		setIsSubmitting(true);
		dispatch(cancelCounteroffer(id)).finally(() => {
			setIsSubmitting(false);
			onClose();
		});
	};

	return (
		<ConfirmationModal
			loading={isSubmitting}
			isOpen={isOpen}
			onClose={onClose}
			onAgree={onCancel}
			title={t.cancelCounterofferText}
			text={t.cancelCounterofferModalText}
			submitText={t.yesText}
			cancelText={t.noText}
		/>
	);
};
